import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  font-size: 18px;
  line-height: 1.8;
  color: #2d3748;
`

const HeroSection = styled.section`
  padding-top: 100px;
  padding-bottom: 60px;
  background: linear-gradient(135deg, #0d9488 0%, #0891b2 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(13, 148, 136, 0.9) 0%, rgba(8, 145, 178, 0.9) 100%);
    z-index: 0;
  }

  @media (max-width: 800px) {
    padding-top: 80px;
    padding-bottom: 40px;
  }
`

const MaxWidthContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 1;

  @media (max-width: 800px) {
    padding: 0 20px;
  }
`

const HeroContent = styled.div`
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`

const ProductLogo = styled.h1`
  font-size: 56px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 20px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 800px) {
    font-size: 40px;
  }
`

const HeroTagline = styled.p`
  font-size: 24px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 30px;

  @media (max-width: 800px) {
    font-size: 20px;
  }
`

const HeroDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 40px;
`

const CTAButton = styled.button`
  background: #ffffff;
  color: #2563eb;
  font-size: 18px;
  font-weight: 700;
  padding: 18px 50px;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
  transition: all 500ms ease-in-out;

  &:hover {
    transform: translate3d(0, -4px, 0);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    background: #FFD93D;
    color: #2d3748;
  }

  @media (max-width: 800px) {
    width: 100%;
    padding: 18px 40px;
  }
`

const Section = styled.section`
  padding: 100px 0;

  @media (max-width: 800px) {
    padding: 60px 0;
  }
`

const OverviewSection = styled(Section)`
  background: linear-gradient(180deg, #f7fafc 0%, #edf2f7 100%);
`

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  line-height: 1.3;
  color: #1a202c;
  text-align: center;
  margin-bottom: 20px;

  @media (max-width: 800px) {
    font-size: 28px;
  }
`

const SectionSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #4a5568;
  margin-bottom: 30px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`

const FeatureCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0px 4px 20px rgba(37, 99, 235, 0.1);
  transition: all 500ms ease-in-out;
  text-align: center;

  &:hover {
    transform: translate3d(0, -10px, 0);
    box-shadow: 0px 12px 40px rgba(37, 99, 235, 0.2);
  }
`

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  margin: 0 auto 25px;
`

const FeatureTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 15px;
`

const FeatureDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
`

const BenefitsSection = styled(Section)`
  background: linear-gradient(180deg, #ffffff 0%, #f7fafc 100%);
`

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const BenefitItem = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`

const BenefitIcon = styled.div`
  font-size: 32px;
  color: #2563eb;
  flex-shrink: 0;
`

const BenefitContent = styled.div``

const BenefitTitle = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 10px;
`

const BenefitText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
`

const HowItWorksSection = styled(Section)`
  background: linear-gradient(180deg, #edf2f7 0%, #e2e8f0 100%);
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const StepCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  position: relative;
  box-shadow: 0px 4px 20px rgba(37, 99, 235, 0.1);
`

const StepNumber = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin: 0 auto 20px;
`

const StepTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 10px;
`

const StepDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #4a5568;
`

const CTASection = styled(Section)`
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  color: white;
  text-align: center;
`

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;

  @media (max-width: 800px) {
    font-size: 28px;
  }
`

const CTADescription = styled.p`
  font-size: 18px;
  margin-bottom: 40px;
  color: rgba(255, 255, 255, 0.95);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`

const CTAButtonWhite = styled(CTAButton)`
  background: white;
  color: #2563eb;

  &:hover {
    background: #FFD93D;
    color: #2d3748;
  }
`

const EMedicarematch = () => {
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const features = [
    {
      icon: '🔍',
      title: 'Comprehensive Plan Comparison',
      description: 'Compare multiple Medicare and health insurance plans side-by-side with detailed coverage information, costs, and benefits to make informed decisions.'
    },
    {
      icon: '💰',
      title: 'Cost Analysis & Savings',
      description: 'Analyze premiums, deductibles, copays, and out-of-pocket maximums to find plans that fit your budget while maximizing coverage.'
    },
    {
      icon: '🏥',
      title: 'Provider Network Search',
      description: 'Check if your preferred doctors, hospitals, and specialists are in-network to ensure continuity of care with your chosen plan.'
    },
    {
      icon: '💊',
      title: 'Prescription Drug Coverage',
      description: 'Verify coverage for your medications and compare drug costs across different plans to minimize your prescription expenses.'
    },
    {
      icon: '📊',
      title: 'Personalized Recommendations',
      description: 'Get tailored plan suggestions based on your health needs, budget, location, and coverage preferences using advanced matching algorithms.'
    },
    {
      icon: '📱',
      title: 'Easy-to-Use Interface',
      description: 'Navigate an intuitive platform designed for simplicity, making health plan comparison accessible to users of all technical skill levels.'
    }
  ]

  const benefits = [
    {
      icon: '✅',
      title: 'Save Time & Money',
      description: 'Quickly compare dozens of plans in minutes instead of hours, and identify cost-saving opportunities you might have missed.'
    },
    {
      icon: '🎯',
      title: 'Find the Perfect Fit',
      description: 'Match your unique healthcare needs with the right plan, ensuring you get the coverage you need without paying for what you don\'t.'
    },
    {
      icon: '🔒',
      title: 'Secure & Confidential',
      description: 'Your personal health and financial information is protected with enterprise-grade security and privacy measures.'
    },
    {
      icon: '📞',
      title: 'Expert Support',
      description: 'Access knowledgeable support representatives who can answer questions and guide you through the enrollment process.'
    },
    {
      icon: '🔄',
      title: 'Annual Review Reminders',
      description: 'Stay informed about plan changes and receive reminders during open enrollment to ensure you always have the best coverage.'
    },
    {
      icon: '📈',
      title: 'Track Coverage Changes',
      description: 'Monitor updates to your plan benefits, costs, and provider networks throughout the year to avoid surprises.'
    }
  ]

  const steps = [
    {
      number: '1',
      title: 'Enter Your Information',
      description: 'Provide your zip code, age, and basic health information to get started.'
    },
    {
      number: '2',
      title: 'Review Your Options',
      description: 'Browse personalized plan recommendations tailored to your needs and budget.'
    },
    {
      number: '3',
      title: 'Compare Plans',
      description: 'Compare coverage details, costs, networks, and prescription drug coverage side-by-side.'
    },
    {
      number: '4',
      title: 'Choose & Enroll',
      description: 'Select the best plan for you and complete enrollment securely through our platform.'
    }
  ]

  return (
    <Container>
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <ProductLogo>eMedicarematch</ProductLogo>
            <HeroTagline>Find Your Perfect Health Plan with Confidence</HeroTagline>
            <HeroDescription>
              Simplify your Medicare and health insurance shopping with our intelligent comparison platform.
              Compare plans, analyze costs, and make informed decisions about your healthcare coverage—all in one place.
            </HeroDescription>
            <CTAButton onClick={() => navigate('/contact')}>
              Request a Demo
            </CTAButton>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      <OverviewSection>
        <MaxWidthContainer>
          <SectionTitle>Choosing the Right Health Plan Made Simple</SectionTitle>
          <SectionSubtitle>
            eMedicarematch is an innovative web platform designed to help you navigate the complex world of
            Medicare and health insurance. Our advanced comparison tools and personalized recommendations
            empower you to find the perfect plan that meets your healthcare needs and fits your budget.
          </SectionSubtitle>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </MaxWidthContainer>
      </OverviewSection>

      <BenefitsSection>
        <MaxWidthContainer>
          <SectionTitle>Why Choose eMedicarematch?</SectionTitle>
          <SectionSubtitle>
            We take the confusion out of health plan selection with tools and resources designed to
            simplify your decision-making process and ensure you get the best possible coverage.
          </SectionSubtitle>

          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>{benefit.icon}</BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitText>{benefit.description}</BenefitText>
                </BenefitContent>
              </BenefitItem>
            ))}
          </BenefitsList>
        </MaxWidthContainer>
      </BenefitsSection>

      <HowItWorksSection>
        <MaxWidthContainer>
          <SectionTitle>How It Works</SectionTitle>
          <SectionSubtitle>
            Getting started with eMedicarematch is easy. Follow these simple steps to find your ideal health plan.
          </SectionSubtitle>

          <StepsGrid>
            {steps.map((step, index) => (
              <StepCard key={index}>
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepCard>
            ))}
          </StepsGrid>
        </MaxWidthContainer>
      </HowItWorksSection>

      <CTASection>
        <MaxWidthContainer>
          <CTATitle>Ready to Find Your Perfect Health Plan?</CTATitle>
          <CTADescription>
            Join thousands of satisfied users who have found better coverage and saved money with eMedicarematch.
            Schedule a personalized demo and see how our platform can help you find the perfect health plan today.
          </CTADescription>
          <CTAButtonWhite onClick={() => navigate('/contact')}>
            Request a Demo
          </CTAButtonWhite>
        </MaxWidthContainer>
      </CTASection>
    </Container>
  )
}

export default EMedicarematch
